import type React from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'state';
import styled from 'styled-components';

import { signin } from 'actions/auth';
import useServices from 'app/services';
import { RequestError } from 'app/utils/requests/fetch';
import logger from 'utils/logger';

import ErrorComponent from 'app/ui/components/atoms/error';
import LoadingWithOverlay from 'app/ui/components/molecules/loading-with-overlay';
import HtmlTitle from 'src/app/ui/components/atoms/html-title';

const Layout = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
`;

type PortalAuthError = 'missing_token' | 'not_provisioned' | 'generic';

// The token exchange refuses an account with no provisioned gym with a 4xx body of
// { error: 'invalid_request', message: 'gym_not_provisioned' }. RequestError builds its
// own `message` from `body.error`, so `error.message` is only ever 'invalid_request' —
// the code that tells the refusals apart lives on `error.body.message`.
const isNotProvisioned = (error: unknown): error is RequestError =>
  error instanceof RequestError && error.body?.message === 'gym_not_provisioned';

const PortalAuthorizationPage = () => {
  const services = useServices();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  const [error, setError] = useState<PortalAuthError | null>(null);

  useEffect(() => {
    const handleTokenExchange = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (!token) {
          logger.error({ message: 'No Cognito authorization token provided' });
          return setError('missing_token');
        }

        const tokenPayload = await services.cognito.exchangeCognitoToken(token);

        if (tokenPayload?.accessToken) {
          dispatch(signin(tokenPayload));

          // Sub-plan S: the hyrox-api mirror session comes from the same Cognito token. mint() never
          // rejects and reports its own failures, so a mirror problem cannot affect the login.
          services.hyroxMirror?.session.mint(token);

          // Check for redirect parameters
          const params = new URLSearchParams(location.search);
          const redirectTo = params.get('redirect_to');
          const verifyRedirectTo = params.get('verify_redirect_to');

          if (redirectTo) {
            const decodedRedirect = decodeURIComponent(redirectTo);
            setTimeout(() => {
              window.location.href = decodedRedirect;
            }, 500);
          } else if (verifyRedirectTo) {
            // Handle verify-onefiit redirect
            const decodedRedirect = decodeURIComponent(verifyRedirectTo);
            setTimeout(() => {
              window.location.href = decodedRedirect;
            }, 500);
          } else {
            setTimeout(() => navigate('/'), 500);
          }
        }
      } catch (error) {
        // An unprovisioned account is an expected business refusal, not an exception, so it is
        // warned about rather than reported to Sentry as a failure of ours.
        if (isNotProvisioned(error)) {
          logger.warn({
            message: 'Portal sign-in refused: account not provisioned',
            context: { status: error.status, code: error.body.message },
          });
          return setError('not_provisioned');
        }

        logger.error({ message: 'Failed to authenticate with the portal', error });
        setError('generic');
      }
    };

    handleTokenExchange();
  }, [services, dispatch, location.search, navigate]);

  const renderContent = (): React.ReactNode => {
    if (!error) {
      return <LoadingWithOverlay />;
    }

    if (error === 'not_provisioned') {
      return (
        <ErrorComponent
          title={intl.formatMessage({ id: 'portalAuthorization.notProvisioned.title' })}
          message={intl.formatMessage({ id: 'portalAuthorization.notProvisioned.message' })}
        />
      );
    }

    return <ErrorComponent />;
  };

  return (
    <>
      <HtmlTitle title="Portal Authorization" />
      <Layout>{renderContent()}</Layout>
    </>
  );
};

export default PortalAuthorizationPage;
